<template>
  <UIGridSkeleton
    v-if="isLoading"
    :columns
    :sm
    message="Loading prices..."
  />
  <UIGrid
    v-else
    v-model:sort="sort"
    data-refid="assetPricesList"
    :columns
    :edit-row-id
    :items
    :sm
    :lazy="50"
    :collapse-width="430"
    scrollable
    editable-rows
  >
    <template #cellDate="{ editRow, item, size }">
      <div v-if="editRow" @mouseup.stop @mousedown.stop>
        <UIPreloader v-if="processing" />
        <UIInputDate v-else v-model="formData.date" :size placeholder="Date" />
      </div>
      <div v-else :class="getSourceClasses(item.source)">
        {{ stringToLocalDateString(item.date) }}
      </div>
    </template>
    <template #cellClose="{ editRow, item, size }">
      <div v-if="editRow" class="asset-drawer-prices-table__price-input" @mouseup.stop @mousedown.stop>
        <UIInputNumber
          v-if="!processing"
          v-model="formData.close"
          :size
          placeholder="Close"
          @ready="handleReady"
        />
        <UIInputSelect
          v-if="!processing"
          v-model="formData.source"
          v-tooltip="{
            content: SOURCE_TOOLTIP,
            html: true
          }"
          :data="ASSET_PRICE_SOURCE_EDITABLE"
          :size="size"
        />
      </div>
      <div v-else :class="getSourceClasses(item.source)">
        {{ priceFormatter(item[currentCloseField]) }}
      </div>
    </template>
    <template #cellSource="{ editRow, item, size }">
      <div
        v-if="editRow"
        class="asset-drawer-prices-table__actions"
        @mouseup.stop
        @mousedown.stop
      >
        <UIButton
          title="Approve changes"
          :leading="CheckIcon"
          :disabled="isApproveButtonDisabled"
          type="button"
          variant="secondary"
          :size
          @click="handleApproveEditClick"
        />
        <UIButton
          title="Cancel"
          :leading="XMarkIcon"
          :disabled="processing"
          type="button"
          variant="danger"
          fill="light"
          :size
          @click="handleCancelEdit"
        />
      </div>
      <div v-else :class="getSourceClasses(item.source)">
        {{ item.source }}
      </div>
    </template>
    <template v-if="!isReadonly" #actions="{ item, size }">
      <template
        v-if="
          item.source === AssetPriceSource.MANUAL ||
            item.source === AssetPriceSource.OVERRIDE
        "
      >
        <UIButton
          label="Edit"
          :leading="PencilIcon"
          variant="secondary"
          fill="light"
          :size
          @click="handleClickEdit(item)"
        />
        <UIButton
          label="Delete"
          :leading="TrashIcon"
          variant="danger"
          fill="light"
          :size
          @click="handleClickDelete(item)"
        />
      </template>
    </template>
  </UIGrid>
</template>

<script setup lang="ts">
import { computed, inject, markRaw, reactive, ref, toRef } from 'vue'

import { AssetClass } from '..'
import { AssetPrice } from '../utils/types'
import { AssetPriceSource } from './utils/enums'
import { ASSET_PRICE_SOURCE_EDITABLE, SOURCE_TOOLTIP } from './utils/const'
import { AssetPriceCloseField, ReadonlyMode, Sort } from '@types'

import { READONLY_MODE } from '@/const/common'

import { priceFormatter } from '@/helpers/numbers'
import { handleCatchedError } from '@/helpers/common'
import { stringToLocalDateString } from '@/helpers/dates'

import useGridSort from '@/components/hooks/gridSort'

import { useAssetPricesStore } from '@/store/assets/prices'
import { useModalsStore } from '@/store/modals'

import { UIButton } from '@ui/buttons'
import { UIPreloader } from '@ui/core'
import {
  UIInputDate,
  UIInputNumber,
  UIInputSelect,
} from '@ui/fields'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { UIDialogRemove } from '@ui/modals'
import {
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'

type Props = {
  instance: AssetClass
  inputItems: AssetPrice[]
  loading: boolean
}

type Emits = {
  'refresh:analytics': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modelValue = defineModel<AssetPrice>()
const isCreatingMode = defineModel<boolean>('creating')

defineExpose({
  clear() {
    editRowId.value = undefined
  },
})

const assetPricesStore = useAssetPricesStore()
const modalsStore = useModalsStore()

const editRowId = ref<number>()

const formData = reactive<Pick<AssetPrice, 'date' | 'close' | 'source'>>({
  date: null,
  close: null,
  source: AssetPriceSource.MANUAL,
})

const processing = ref(false)

const priceSource = ref(AssetPriceSource.ALL)

const removedPrice = ref<AssetPrice>()

const sort = ref<Sort[]>()

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const isLoading = computed(
  () =>
    props.loading ||
    assetPricesStore.loading ||
    assetPricesStore.loadingSecurityMaster ||
    assetPricesStore.loadingAction,
)

const isFormDirty = computed(
  () =>
    formData.date !== modelValue.value?.date ||
    formData.close !== modelValue.value.close ||
    formData.source !== modelValue.value.source,
)

const isApproveButtonDisabled = computed(
  () =>
    !formData.date ||
    formData.close === null ||
    !isFormDirty.value ||
    processing.value,
)

const currentCloseField = computed<AssetPriceCloseField>(() =>
  priceSource.value === AssetPriceSource.SECURITY_MASTER_ADJ
    ? 'adjusted_close'
    : 'close',
)

const sm = '1fr 1fr 1fr'
const columns = computed(() => [
  {
    name: 'date',
    caption: 'Date',
    default: '1fr',
    hideCaptionIfCollapsed: true,
  },
  {
    name: currentCloseField.value,
    caption: 'Close',
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    default: '1fr',
  },
  {
    name: 'source',
    caption: 'Source',
    cellClasses: 'ui-grid__cell--span-2',
    lineClamp: 2,
    default: '105px',
  },
])

const inputItems = computed(() => props.inputItems)
const items = useGridSort<AssetPrice>(sort, columns, inputItems, ['close'], toRef([
  function (item: AssetPrice) {
    return item.source !== AssetPriceSource.OVERRIDE
  },
]))

const getSourceClasses = (source: AssetPriceSource) => {
  if (source === AssetPriceSource.OVERRIDE) {
    return 'asset-drawer-prices-table__source-override'
  }
}

const handleClickEdit = (item: AssetPrice) => {
  isCreatingMode.value = false
  editRowId.value = item.id
  formData.date = item.date
  formData.close = item.close
  formData.source = item.source
}

const handleClickDelete = (item: AssetPrice) => {
  isCreatingMode.value = false
  removedPrice.value = item
  const key = `asset-remove-price-${item.id}`
  const modalInstance = modalsStore.init(key, markRaw(UIDialogRemove))
  modalInstance?.open(modalsStore.getZIndex(), {
    title: `Delete asset price '${removedPrice.value?.date}'`,
    message: 'Are you sure you want to delete this price?',
    label: 'Delete',
    onRemove: handleDelete
  })
}

const handleDelete = async () => {
  const id = removedPrice.value?.id
  if (!id) return
  try {
    removedPrice.value = undefined
    await assetPricesStore.destroy(id)
    const priceIndex = props.instance.prices?.findIndex(item => item.id === id)
    if (priceIndex !== undefined && priceIndex > -1) {
      props.instance.prices?.splice(priceIndex, 1)
    }
    emit('refresh:analytics')
  } catch (e) {
    handleCatchedError(e as string, { id })
  }
}

const handleCancelEdit = () => {
  editRowId.value = undefined
}

const handleApproveEditClick = async () => {
  if (!formData.date || formData.close === null || !editRowId.value) return
  processing.value = true
  setTimeout(async () => {
    const data = {
      id: editRowId.value,
      date: formData.date,
      close: formData.close,
      source: formData.source,
    }
    try {
      const result = await assetPricesStore.update(data)
      if (result) {
        if (props.instance.prices) {
          const priceIndex = props.instance.prices.findIndex(
            item => item.id === result.id,
          )
          props.instance.prices[priceIndex] = result as AssetPrice
        }
        editRowId.value = undefined
      }
      emit('refresh:analytics')
    } catch (e) {
      handleCatchedError(e as string, data)
    } finally {
      processing.value = false
    }
  })
}

const handleReady = (el: HTMLInputElement) => {
  el?.focus()
}
</script>

<style>
.asset-drawer-prices-table__actions {
  @apply flex gap-2;
}

.asset-drawer-prices-table__price-input {
  @apply flex gap-2;

  .v-popper {
    @apply w-[5.25rem];
    @apply shrink-0;
  }
}

.asset-drawer-prices-table__source-override {
  @apply font-semibold text-yellow-600;
}
</style>
